import {
  createCredential,
  linkCredentialToBusinessEntity,
  updateCredential,
  useCredentials,
} from '@commenda/api';
import { Alert, Button, Icons } from '@commenda/kakashi';
import { IBusinessEntity } from '@commenda/types';
import {
  CreateCredentialProps,
  CredentialType,
  ICredential,
  ICredentialBlock,
  TUpdateCredentialProps,
} from '@commenda/types/src/credential/types';
import { RegistrationHelper } from '@commenda/utils';
import { useNotification } from '../../../../../hooks/useNotification';
import CredentialBlockFormFields from './CredentialBlock';

type CredentialFormProps = {
  supportedCredentialBlocks: ICredentialBlock[];
  setSupportedCredentialBlocks: (
    supportedCredentialBlocks: ICredentialBlock[]
  ) => void;
  irsServicesCredential: ICredential;
  businessEntity: IBusinessEntity;
  notes: string;
  setNotes: (notes: string) => void;
  onContinue: () => void;
  onBack: () => void;
};

export const CredentialForm = ({
  irsServicesCredential,
  supportedCredentialBlocks,
  setSupportedCredentialBlocks,
  businessEntity,
  notes,
  setNotes,
  onContinue,
  onBack,
}: CredentialFormProps) => {
  const { mutate: mutateCredentials } = useCredentials({
    businessEntityId: businessEntity.id,
  });
  const { setNotification } = useNotification();

  const validateSecurityDetails = () => {
    // For pre-existing credentials, validate all credential blocks
    const emptyCredentialBlocks = supportedCredentialBlocks.filter(
      (block) => !block.value?.trim()
    );

    return emptyCredentialBlocks.length === 0;
  };

  const handleSave = async () => {
    const portalURL = RegistrationHelper.US_IRS_E_SERVICES_PORTAL_URL;

    if (!validateSecurityDetails()) return;

    if (irsServicesCredential) {
      const updateData: TUpdateCredentialProps = {
        notes,
        portalURL,
        blocks: supportedCredentialBlocks.map((block) => ({
          id: block.id,
          value: block.value,
        })),
        blocksToDelete: [],
      };

      const response = await updateCredential(
        irsServicesCredential.id,
        updateData
      );

      if (!response) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong while updating credential, please try again or contact support if you need help.',
        });
        return;
      }
      mutateCredentials();
      onContinue();
      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Credential updated successfully.',
      });
    } else {
      // Create new credential

      const data: CreateCredentialProps = {
        credentialType: CredentialType.CORPORATE_TAX,

        country: businessEntity.generalCorporationInfo.incorporationCountry,
        blocks: [
          ...supportedCredentialBlocks.map((credBlock) => ({
            isCustomBlock: false,
            key: credBlock.key,
            type: credBlock.type,
            value: credBlock.value,
          })),
        ],
        internalIdentifier:
          RegistrationHelper.US_IRS_E_SERVICES_INTERNAL_IDENTIFIER,
        portalURL,
        ...(notes ? { notes } : {}),
      };

      const response = await createCredential(data);

      if (!response) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong while adding credential, please try again or contact support if you need help.',
        });
        return;
      }

      const createdCredentialResponse = (await response.json()) as {
        credential: { id: string };
      };

      const createdCredentialId = createdCredentialResponse.credential.id;

      const linkCredentialToBusinessEntityResponse =
        await linkCredentialToBusinessEntity(
          businessEntity.id,
          createdCredentialId
        );

      if (!linkCredentialToBusinessEntityResponse) {
        setNotification({
          show: true,
          valid: false,
          title: 'Error',
          message:
            'Something went wrong while linking credential to business entity, please try again or contact support if you need help.',
        });
        return;
      }

      mutateCredentials();
      onContinue();

      setNotification({
        show: true,
        valid: true,
        title: 'Success',
        message: 'Credential added successfully.',
      });
    }
  };

  const onSkip = () => {
    onContinue();
  };

  return (
    <div className="flex flex-col gap-md">
      <Alert
        variant="info"
        title=""
        description="Ensure your credentials are accurate and up to date."
        icon={
          <Icons.InfoCircle className="size-4 text-utility-blue-500 mt-0.5" />
        }
      />
      <div className="flex flex-col">
        <h2 className="text-md mt-2xl">IRS Online Services</h2>
        <p className="txt-small-regular text-text-tertiary mb-2xl">
          This will be required for tax filing access.
        </p>
      </div>
      <CredentialBlockFormFields
        notes={notes}
        setNotes={setNotes}
        supportedCredentialBlocks={supportedCredentialBlocks}
        setSupportedCredentialBlocks={setSupportedCredentialBlocks}
      />
      <div className="flex gap-xl">
        <Button size="lg" variant="secondary" onClick={onBack}>
          <Icons.ArrowLeft className="size-5" />
          <span>Back</span>
        </Button>

        <Button size="lg" variant="secondary" onClick={onSkip}>
          <span>Skip</span>
        </Button>

        <Button
          size="lg"
          onClick={handleSave}
          variant="secondary"
          disabled={!validateSecurityDetails()}
        >
          <span>Continue</span>
          <Icons.ArrowRight className="size-5" />
        </Button>
      </div>
    </div>
  );
};
